import React, { useEffect, useState, useRef } from 'react';
import { useSpring, useSprings, config, animated } from 'react-spring';

import * as S from './styles';

type TabMenuItems = {
  items: string[];
  onItemSelect: (x: number) => void;
  activeIndex?: number;
  ariaLabel: string;
  ariaControls: string;
};

export const TabMenu: React.FC<TabMenuItems> = ({
  items,
  onItemSelect,
  activeIndex,
  ariaLabel,
  ariaControls,
}) => {
  const [key, setKey] = useState<number>(activeIndex || 0);
  const [offsetLeft, setOffsetLeft] = useState<number>(0);
  const [width, setWidth] = useState<number>(64);
  const activeTab = useRef<HTMLButtonElement>(null);

  const [props] = useSpring(
    () => ({
      width: width,
      transform: `translateX(${offsetLeft}px)`,
      config: { ...config.stiff, friction: 32, tension: 200 },
    }),
    [width, offsetLeft, window.innerWidth, key],
  );

  const [transitions, set] = useSprings(items.length, i => {
    return {
      opacity: 1,
    };
  });

  const handleClick = (
    x: number,
    e: React.SyntheticEvent<HTMLButtonElement>,
  ) => {
    const element = e.target as HTMLButtonElement;
    if (element) {
      setOffsetLeft(element.offsetLeft);
      setWidth(element.offsetWidth);
      setKey(x);
      onItemSelect(x);
    }

    handleMouseLeave();
  };

  const handleMouseEnter = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const element = e.target as HTMLButtonElement;
    const elementId = element.dataset.index;
    set(i => {
      if (Number(elementId) !== i) {
        return { opacity: 0.5 };
      }

      return { opacity: 1 };
    });
  };

  const handleMouseLeave = () => {
    set(() => ({ opacity: 1 }));
  };

  useEffect(() => {
    const currentActive = activeTab.current;
    if (activeIndex && currentActive) {
      setOffsetLeft(currentActive.offsetLeft);
      setWidth(currentActive.offsetWidth);
      setKey(activeIndex);
      onItemSelect(activeIndex);
    }
  }, [activeIndex]);

  return (
    <S.TabMenuWrapper>
      <S.Tabs data-testid="tab-menu-tabs" role="tablist" aria-label={ariaLabel}>
        {transitions.map(({ opacity }, i) => {
          return (
            <S.Item key={`tab-${i}`}>
              {/* @ts-ignore */}
              <animated.div style={{ opacity }}>
                <S.Tab
                  data-index={i}
                  ref={activeIndex && activeIndex === i ? activeTab : null}
                  onClick={e => handleClick(i, e)}
                  data-testid="tab-menu-tab"
                  role="tab"
                  aria-selected={i === key}
                  aria-controls={ariaControls}
                  onMouseEnter={e => handleMouseEnter(e)}
                  onMouseLeave={handleMouseLeave}
                >
                  {items[i]}
                </S.Tab>
              </animated.div>
            </S.Item>
          );
        })}
      </S.Tabs>
      <S.AnimatedActiveIndicator style={props} />
    </S.TabMenuWrapper>
  );
};

export default TabMenu;
